import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GPG_KEYS } from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as gpgKeysApi from '../services/api';
import { GenerateGPGKeyRequest } from '../types';
import { convertToGPGKey } from '../utils';

const GPG_KEYS_QUERY_KEY = [GPG_KEYS];

const useGPGKeys = () => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const {
    data: gpgKeysResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: GPG_KEYS_QUERY_KEY,
    queryFn: gpgKeysApi.listGPGKeys,
  });

  const gpgKeys = useMemo(() => {
    if (!gpgKeysResponse) return [];
    return gpgKeysResponse.map(convertToGPGKey);
  }, [gpgKeysResponse]);

  const generateKeyMutation = useMutation({
    mutationFn: async (request: GenerateGPGKeyRequest) => {
      const response = await gpgKeysApi.generateGPGKey(request);
      const formattedKey = convertToGPGKey(response);
      return formattedKey;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GPG_KEYS_QUERY_KEY });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to generate GPG key',
        variant: 'error',
      });
    },
  });

  const revokeKeyMutation = useMutation({
    mutationFn: (keyId: string) => gpgKeysApi.revokeGPGKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GPG_KEYS_QUERY_KEY });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to revoke GPG key',
        variant: 'error',
      });
    },
  });

  const activeKey = useMemo(
    () => gpgKeys.find((key) => key.isActive),
    [gpgKeys],
  );

  return {
    gpgKeys,
    activeKey,
    isLoading,
    error,
    generateKey: generateKeyMutation.mutate,
    isGenerating: generateKeyMutation.isLoading,
    revokeKey: revokeKeyMutation.mutate,
    isRevoking: revokeKeyMutation.isLoading,
  };
};

export default useGPGKeys;
